import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useMutation } from 'urql';
import { useToast } from '@chakra-ui/react';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { AggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import { useSnapper } from 'src/components/viewers/SheetsViewerV2/hooks/useSnapper';
import {
  DrawingMode,
  ShapesDrawingTool,
} from 'src/components/viewers/SheetsViewerV2/autodesk/tools/ShapesDrawingTool';
import { DrainLineDrawingTool } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/DrainLineDrawingTool';
import { WedgeDrawingTool } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/WedgeDrawingTool';
import {
  ShapeDrawingResultType,
  SlopedInsulationCrossSection,
} from 'src/components/orderContractor/pdf-shapes/SheetShapeDrawing';
import { ViewerExtension } from 'src/components/viewers/common/utils/autodesk';
import { mapDrawingResults } from 'src/components/viewers/SheetsViewerV2/mappers/mapDrawingResults';
import {
  CreateSheetShapeDocument,
  CreateSheetShapeInput,
  UpdateSheetShapeDocument,
  UpdateSheetShapeInput,
  SheetShapeDeepFragment,
  GetMagicSheetShapeDocument,
  SheetScaleType,
  EventResource,
  EventType,
  BulkUpsertSparkelPropertiesDocument,
  BulkDeleteSparkelPropertiesDocument,
  GetSparkelPropertiesForProjectQuery,
} from 'src/gql/graphql';
import { ApiCoordinatesConverter } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';
import { getUnitScaleFromCalibration } from 'src/domain/sheet-calibration';
import { Point2 } from 'src/domain/geometry/geometric-types';
import { updateWedgePropsV2 } from 'src/hooks/protan-special-handling';
import {
  Diamond,
  KileTypeSKU,
} from 'src/components/orderContractor/pdf-shapes/protan/SheetWedgeShapeDrawing';
import Edit2D = Autodesk.Extensions.Edit2D;
import { useEventHistory } from 'src/hooks/event-history';
import { getShapeType } from 'src/hooks/shapes';
import {
  getInsulationAndFasteners,
  Product,
  InnerOuter,
  getSectioning,
} from 'src/components/orderContractor/ProductChoices';
import { updateSparkelProperties } from 'src/components/orderContractor/pdf-shapes/protan/useTaperedInsulation';
import { getRoofShapeFromProperties } from 'src/components/orderContractor/pdf-shapes/SheetDrainLineDrawing';

type Props = {
  activeTool: ActiveTool;
  projectId: string;
  activeSheetData: ActiveSheetData | null;
  viewer: AggregatedView['viewer'] | undefined;
  calibration?: SheetScaleType | null;
  renderedSheetShapes?: Record<string, SheetShapeDeepFragment[]>;
  propertyData?: GetSparkelPropertiesForProjectQuery;
  displayUnitSuffix?: string;
};

type DrawingResults = ReturnType<typeof mapDrawingResults> | { valid: false };

export function useShapesDrawingTool({
  activeTool,
  projectId,
  viewer,
  activeSheetData,
  calibration,
  renderedSheetShapes,
  propertyData,
  displayUnitSuffix = 'm',
}: Props) {
  const toast = useToast();
  const [createShapeResult, createShapeMutation] = useMutation(
    CreateSheetShapeDocument
  );
  const [, getMagicSheetShape] = useMutation(GetMagicSheetShapeDocument);
  const [, bulkUpsertSparkelProps] = useMutation(
    BulkUpsertSparkelPropertiesDocument
  );
  const [, bulkDeleteSparkelProps] = useMutation(
    BulkDeleteSparkelPropertiesDocument
  );

  const [edit2D, setEdit2D] = useState<Autodesk.Extensions.Edit2D | null>(null);
  const [results, setResults] = useState<DrawingResults>();
  const [drainLineData, setDrainLineData] = useState<{
    crossSection: SlopedInsulationCrossSection;
    roofShapeDbId: number;
    productAreas: Record<string, number>;
    roofUValue: number | null;
    totalVolume: number;
  } | null>(null);
  const [isUpdatingProperties, setIsUpdatingProperties] = useState(false);
  const [shapeToEdit, setShapeToEdit] = useState<SheetShapeDeepFragment | null>(
    null
  );
  const [updateShapeResult, updateShapeMutation] = useMutation(
    UpdateSheetShapeDocument
  );
  const [isMagicPolygonLoading, setIsMagicPolygonLoading] = useState(false);
  const toolRef = useRef<
    ShapesDrawingTool | DrainLineDrawingTool | WedgeDrawingTool | null
  >(null);
  const lastProcessedPointRef = useRef<string | null>(null);
  const carriedPointsRef = useRef<THREE.Vector3[][] | null>(null);
  const prevDrawingModeRef = useRef<DrawingMode>(DrawingMode.Polygon);

  const hasResults = useMemo(
    () =>
      Array.isArray(results)
        ? results.length > 0 && results.every((shape) => shape.valid)
        : results?.valid || false,
    [results]
  );
  const [drawingMode, setDrawingMode] = useState<DrawingMode>(
    DrawingMode.Polygon
  );
  const [minHeight, setMinHeight] = useState(0.26);
  const [initialPoints, setInitialPoints] = useState<THREE.Vector3[][] | null>(
    null
  );
  const { snapper } = useSnapper(viewer);
  const isDrawing = useMemo(
    () => activeTool === ActiveTool.Drawing,
    [activeTool]
  );

  const { logEvent } = useEventHistory(projectId);

  useEffect(() => {
    if (!viewer) return;

    const edit2D = viewer.getExtension(ViewerExtension.Edit2D) as Edit2D;

    if (!edit2D.defaultContext) {
      edit2D.registerDefaultTools();
    }

    setEdit2D(edit2D);
  }, [viewer]);

  // Update tool when MagicPolygon results change
  useEffect(() => {
    if (
      toolRef.current &&
      'updateMagicPolygonResults' in toolRef.current &&
      drawingMode === DrawingMode.MagicPolygon &&
      results
    ) {
      (toolRef.current as ShapesDrawingTool).updateMagicPolygonResults(results);
    }
  }, [results, drawingMode]);

  // Update tool loading state for MagicPolygon
  useEffect(() => {
    if (
      toolRef.current &&
      'setMagicPolygonLoading' in toolRef.current &&
      drawingMode === DrawingMode.MagicPolygon
    ) {
      (toolRef.current as ShapesDrawingTool).setMagicPolygonLoading(
        isMagicPolygonLoading
      );
    }
  }, [isMagicPolygonLoading, drawingMode]);

  // Update DrainLineDrawingTool minHeight when it changes
  useEffect(() => {
    if (
      toolRef.current &&
      'setMinHeight' in toolRef.current &&
      drawingMode === DrawingMode.DrainLine
    ) {
      (toolRef.current as DrainLineDrawingTool).setMinHeight(minHeight);
    }
  }, [minHeight, drawingMode]);

  // Only sync minHeight from tool when tool is first created (not continuously)
  useEffect(() => {
    if (
      toolRef.current &&
      'getMinHeight' in toolRef.current &&
      drawingMode === DrawingMode.DrainLine
    ) {
      const toolMinHeight = (
        toolRef.current as DrainLineDrawingTool
      ).getMinHeight();
      if (toolMinHeight !== minHeight) {
        setMinHeight(toolMinHeight);
      }
    }
  }, [toolRef.current, drawingMode]);

  // Reset last processed point when drawing mode changes
  useEffect(() => {
    lastProcessedPointRef.current = null;
  }, [drawingMode]);

  // Reset last processed point when drawing starts/stops
  useEffect(() => {
    if (!isDrawing) {
      lastProcessedPointRef.current = null;
    }
  }, [isDrawing]);

  // Handle MagicPolygon updates - extracted to reduce complexity
  const handleMagicPolygonUpdate = useCallback(
    (
      pointsArray: THREE.Vector3[][],
      activeSheetData: ActiveSheetData,
      viewer: Autodesk.Viewing.Viewer3D
    ) => {
      // Check if we have a tool reference
      if (!toolRef.current) return;

      const shapesTool = toolRef.current as ShapesDrawingTool;

      // 1. Check for edited points first (user modified the polygon)
      if (
        'getCurrentEditedMagicPolygon' in shapesTool &&
        typeof shapesTool.getCurrentEditedMagicPolygon === 'function'
      ) {
        const editedPoints = shapesTool.getCurrentEditedMagicPolygon();

        if (editedPoints) {
          // Use mapDrawingResults to process the edited points through the same validation pipeline
          // as regular polygon drawing
          const drawingResults = mapDrawingResults(
            DrawingMode.Polygon,
            editedPoints
          );
          setResults(drawingResults);
          return;
        }
      }

      // 2. If not edited, check for sample points to call the API
      if (
        'getSamplePoints' in shapesTool &&
        typeof shapesTool.getSamplePoints === 'function'
      ) {
        const samplePoints = shapesTool.getSamplePoints();

        if (samplePoints && samplePoints.length > 0) {
          const { viewport, id, page } = activeSheetData;

          // Create a unique key for this set of points to prevent duplicate API calls
          const pointsKey = samplePoints
            .map((p) => `${p.x.toFixed(6)},${p.y.toFixed(6)}`)
            .join('|');

          // Skip if we've already processed these exact points
          if (lastProcessedPointRef.current === pointsKey) {
            return;
          }

          // Convert from viewer coordinates to PDF coordinates
          const converter = new ApiCoordinatesConverter(viewer, viewport);
          const mousePoints = samplePoints.map((point) => {
            const apiPoint = converter.convertToApiPoint(point);
            let adjustedX = apiPoint.x;
            let adjustedY = apiPoint.y;
            if (viewport.viewBox[0] < 0) {
              adjustedX = apiPoint.x - viewport.viewBox[0];
              adjustedY = apiPoint.y - viewport.viewBox[1];
            }
            return {
              foreground: true,
              point: { x: adjustedX, y: adjustedY },
            };
          });

          // Mark these points as processed
          lastProcessedPointRef.current = pointsKey;

          // Set loading state
          setIsMagicPolygonLoading(true);

          // Call the magic polygon API
          getMagicSheetShape({
            input: {
              sheetId: id,
              pageNumber: page,
              mousePoints: mousePoints,
            },
          })
            .then((res) => {
              const ring = res.data?.getMagicSheetShape?.polygon?.points;
              if (ring) {
                // Convert the API response back to the coordinate system expected by the viewer
                const adjustedRing = ring.map((p) => {
                  let adjustedX = p.x;
                  let adjustedY = p.y;
                  if (viewport.viewBox[0] < 0) {
                    adjustedX = p.x + viewport.viewBox[0];
                    adjustedY = p.y + viewport.viewBox[1];
                  }
                  return { x: adjustedX, y: adjustedY };
                });

                const polygon = {
                  polygons: [
                    {
                      exterior: adjustedRing.map(
                        (p) => [p.x, p.y] as [number, number]
                      ),
                      interiors: [],
                    },
                  ],
                };
                setResults({ valid: true, multipolygon: polygon });
              } else {
                setResults({ valid: false });
              }
            })
            .catch((e) => {
              setResults({ valid: false });
            })
            .finally(() => {
              setIsMagicPolygonLoading(false);
            });
        }
      }
    },
    [getMagicSheetShape, toolRef]
  );

  // Handle drain line updates - extracted to reduce complexity
  const handleDrainLineUpdate = useCallback(() => {
    if (
      toolRef.current &&
      'getResults' in toolRef.current &&
      drawingMode === DrawingMode.DrainLine
    ) {
      const drainLineResults = (
        toolRef.current as DrainLineDrawingTool
      ).getResults();
      if (drainLineResults.valid) {
        // Convert drain lines to points array for mapDrawingResults
        const points = drainLineResults.drainLines.flatMap(
          (segment: [Point2, Point2]) => [
            { x: segment[0][0], y: segment[0][1] },
            { x: segment[1][0], y: segment[1][1] },
          ]
        );
        const mappedResults = mapDrawingResults(
          drawingMode,
          [points], // Wrap in array to match expected format
          undefined
        );
        setResults(mappedResults);

        // Store drain line data for later use in shape creation/update
        if (
          drainLineResults.crossSection &&
          drainLineResults.roofShapeDbId !== undefined
        ) {
          setDrainLineData({
            crossSection: drainLineResults.crossSection,
            roofShapeDbId: drainLineResults.roofShapeDbId,
            productAreas: drainLineResults.productAreas || {},
            roofUValue: drainLineResults.roofUValue,
            totalVolume: drainLineResults.totalVolume,
          });
        } else {
          setDrainLineData(null);
        }
      } else {
        setResults({ valid: false });
        setDrainLineData(null);
      }
    }
  }, [drawingMode]);

  const updatePointsCallback = useCallback(
    (pointsArray: THREE.Vector3[][]) => {
      try {
        // Always keep the latest points to allow carry-over across polygonal modes
        carriedPointsRef.current = pointsArray;
        if (!viewer || !activeSheetData) {
          throw new Error(
            'Cannot update points without initialized viewer or active sheets data'
          );
        }

        // For MagicPolygon, use the extracted handler
        if (drawingMode === DrawingMode.MagicPolygon) {
          handleMagicPolygonUpdate(pointsArray, activeSheetData, viewer);
          return;
        }

        // For drain lines, use the extracted handler
        if (drawingMode === DrawingMode.DrainLine) {
          handleDrainLineUpdate();
          return;
        }

        const { viewport } = activeSheetData;
        const converter = new ApiCoordinatesConverter(viewer, viewport);
        const convertedPoints = pointsArray.map((points) =>
          points.map((point) => converter.convertToApiPoint(point))
        );

        // For wedges, use the tool's results directly
        if (
          drawingMode === DrawingMode.Wedges &&
          toolRef.current &&
          'getResults' in toolRef.current
        ) {
          const wedgeResults = (
            toolRef.current as WedgeDrawingTool
          ).getResults();
          if (wedgeResults.valid) {
            // Convert Point2[] to ApiPoint[] format for mapDrawingResults
            const points = (wedgeResults.points as Point2[]).map((point) => ({
              x: point[0],
              y: point[1],
            }));
            const mappedResults = mapDrawingResults(
              drawingMode,
              [points], // Wrap in array to match expected format
              wedgeResults.wedgeData
            );
            setResults(mappedResults);
          } else {
            setResults({ valid: false });
          }
        } else {
          const mappedResults = mapDrawingResults(
            drawingMode,
            convertedPoints,
            undefined
          );
          setResults(mappedResults);
        }
      } catch (e) {
        console.error(e);
        setResults({ valid: false });
      }
    },
    [
      viewer,
      activeSheetData,
      drawingMode,
      handleMagicPolygonUpdate,
      handleDrainLineUpdate,
    ]
  );

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (!viewer || !snapper || !edit2D || !activeSheetData) return;

    // Capture previous tool and previous drawing mode before creating a new tool
    const previousTool = toolRef.current;
    const previousMode = prevDrawingModeRef.current;

    let tool: ShapesDrawingTool | DrainLineDrawingTool | WedgeDrawingTool;

    // Determine if we should carry over existing points between polygonal modes
    const isPrevPolyLike = [
      DrawingMode.Polygon,
      DrawingMode.Rectangle,
      DrawingMode.MagicPolygon,
    ].includes(prevDrawingModeRef.current);
    const isNextPolyLike = [
      DrawingMode.Polygon,
      DrawingMode.Rectangle,
      DrawingMode.MagicPolygon,
    ].includes(drawingMode);
    let carryOverPoints: THREE.Vector3[][] | null = null;

    // Helper: convert multipolygon (API coordinates) to viewer points
    const convertMultipolygonToViewerPoints = (mp: {
      polygons: {
        exterior: [number, number][];
        interiors: [number, number][][];
      }[];
    }): THREE.Vector3[][] => {
      const converter = new ApiCoordinatesConverter(
        viewer,
        activeSheetData.viewport
      );
      const allRings: THREE.Vector3[][] = [];
      mp.polygons.forEach((poly) => {
        const exterior = poly.exterior.map(([x, y]) =>
          converter.convertToAutodeskPoint({ x, y })
        );
        const closedExterior = [...exterior];
        if (
          closedExterior.length &&
          !closedExterior[0].equals(closedExterior[closedExterior.length - 1])
        ) {
          closedExterior.push(closedExterior[0]);
        }
        allRings.push(closedExterior);

        poly.interiors.forEach((ring) => {
          const interior = ring.map(([x, y]) =>
            converter.convertToAutodeskPoint({ x, y })
          );
          const closedInterior = [...interior];
          if (
            closedInterior.length &&
            !closedInterior[0].equals(closedInterior[closedInterior.length - 1])
          ) {
            closedInterior.push(closedInterior[0]);
          }
          allRings.push(closedInterior);
        });
      });
      return allRings;
    };

    if (isPrevPolyLike && isNextPolyLike) {
      if (previousMode === DrawingMode.MagicPolygon) {
        // Prefer edited multipolygon from the previous tool if available
        let mp: any = null;
        if (
          previousTool &&
          'getCurrentEditedMagicPolygon' in previousTool &&
          typeof (previousTool as any).getCurrentEditedMagicPolygon ===
            'function'
        ) {
          const edited = (previousTool as any).getCurrentEditedMagicPolygon();
          if (edited) {
            const mapped = mapDrawingResults(DrawingMode.Polygon, edited);
            if (mapped && 'multipolygon' in mapped && mapped.valid) {
              mp = mapped.multipolygon;
            }
          }
        }
        // Fallback to last results if present
        if (
          !mp &&
          results &&
          'multipolygon' in results &&
          (results as any).valid
        ) {
          mp = (results as any).multipolygon;
        }
        if (mp) {
          carryOverPoints = convertMultipolygonToViewerPoints(mp);
        } else {
          carryOverPoints = null;
        }
      } else {
        // For regular polygon/rectangle, carry the raw points
        carryOverPoints = carriedPointsRef.current || null;
      }
    }

    // Create specialized tools for specific drawing modes
    if (drawingMode === DrawingMode.Wedges) {
      // Use dedicated WedgeDrawingTool for wedge drawing
      tool = new WedgeDrawingTool(
        viewer,
        activeSheetData,
        edit2D,
        updatePointsCallback,
        calibration,
        renderedSheetShapes
      );

      // If editing an existing wedge shape, set its initial state
      if (shapeToEdit && shapeToEdit.sheetShapeLine) {
        const points = shapeToEdit.sheetShapeLine.points.map(
          (p) => [p.x, p.y] as Point2
        );
        (tool as WedgeDrawingTool).setInitialState(points);
      }
    } else if (drawingMode === DrawingMode.DrainLine) {
      const shapes = renderedSheetShapes
        ? Object.values(renderedSheetShapes).flat()
        : [];
      tool = new DrainLineDrawingTool(
        viewer,
        activeSheetData,
        edit2D,
        shapes,
        updatePointsCallback,
        propertyData,
        shapeToEdit,
        minHeight,
        calibration
      );
    } else {
      // Use general ShapesDrawingTool for other drawing modes
      tool = new ShapesDrawingTool(
        viewer,
        snapper,
        edit2D,
        activeSheetData,
        drawingMode,
        updatePointsCallback,
        carryOverPoints || initialPoints || undefined,
        calibration,
        renderedSheetShapes,
        displayUnitSuffix
      );
    }

    // Store tool reference
    toolRef.current = tool;

    if (isDrawing) {
      viewer.toolController.registerTool(tool);
      viewer.disableSelection(true);
      viewer.disableHighlight(true);
      viewer.setContextMenu(null);
      viewer.toolController.activateTool(tool.getName());
    } else {
      viewer.toolController.deactivateTool(tool.getName());
      viewer.toolController.deregisterTool(tool);
      setResults(undefined);
      setInitialPoints(null);
      setShapeToEdit(null);
      setDrainLineData(null);
      carriedPointsRef.current = null; // Clear carried points on cancel/exit
    }

    return () => {
      if ('clear' in tool) {
        tool.clear();
      }
      viewer.toolController.deactivateTool(tool.getName());
      viewer.toolController.deregisterTool(tool);
      toolRef.current = null;
    };
  }, [
    isDrawing,
    snapper,
    viewer,
    activeSheetData,
    drawingMode,
    edit2D,
    renderedSheetShapes,
    updatePointsCallback,
    propertyData,
    shapeToEdit,
    activeTool,
    calibration,
    initialPoints,
  ]);
  // Track previous drawing mode for carry-over logic
  useEffect(() => {
    prevDrawingModeRef.current = drawingMode;
  }, [drawingMode]);

  // Update tool context when renderedSheetShapes becomes available
  useEffect(() => {
    if (
      toolRef.current &&
      'updateContext' in toolRef.current &&
      renderedSheetShapes &&
      calibration
    ) {
      const unitScale = calibration
        ? getUnitScaleFromCalibration(calibration)
        : null;
      (toolRef.current as any).updateContext(
        unitScale,
        renderedSheetShapes,
        calibration
      );
    }
  }, [renderedSheetShapes, calibration]);

  // Function to update drain line properties
  const updateDrainLineProperties = useCallback(
    // eslint-disable-next-line complexity
    async (
      sheetShape: SheetShapeDeepFragment,
      crossSection: SlopedInsulationCrossSection,
      roofShapeDbId: number,
      productAreas?: Record<string, number>,
      roofUValue?: number | null,
      totalVolume?: number
    ) => {
      if (!crossSection || !sheetShape || !roofShapeDbId) {
        console.warn('Missing required data for drain line properties update');
        return;
      }

      // Validate cross section data structure
      if (typeof crossSection !== 'object' || crossSection === null) {
        console.error('Invalid cross section data:', crossSection);
        return;
      }

      // Validate that we have the required mutation function
      if (!bulkUpsertSparkelProps) {
        console.error('bulkUpsertSparkelProps mutation is not available');
        return;
      }

      try {
        // Determine previous roof shape (if any) before we change it
        const previousRoofShapeDbId = getRoofShapeFromProperties(
          propertyData,
          sheetShape.dbId,
          sheetShape.urn
        );

        // Store cross section as-is (already in meters)
        const crossSectionForStorage = crossSection;

        // Update cross section property
        await bulkUpsertSparkelProps({
          input: {
            projectId,
            dbIds: [
              {
                modelUrn: sheetShape.urn,
                dbIds: [sheetShape.dbId],
              },
            ],
            thePropertySet: 'CrossSection',
            thePropertyValue: JSON.stringify(crossSectionForStorage),
          },
        });

        // Update roof shape dbId property
        await bulkUpsertSparkelProps({
          input: {
            projectId,
            dbIds: [
              {
                modelUrn: sheetShape.urn,
                dbIds: [sheetShape.dbId],
              },
            ],
            thePropertySet: 'RoofShapeDbId',
            thePropertyValue: roofShapeDbId.toString(),
          },
        });

        // Mark the referenced roof shape as tapered insulation = Yes
        await bulkUpsertSparkelProps({
          input: {
            projectId,
            dbIds: [
              {
                modelUrn: sheetShape.urn,
                dbIds: [roofShapeDbId],
              },
            ],
            thePropertySet: 'Is Tapered Insulation',
            thePropertyValue: 'Yes',
          },
        });

        // If the drain line was previously attached to a different roof shape,
        // and no other drain lines reference that previous roof shape, remove the flag
        if (
          previousRoofShapeDbId &&
          previousRoofShapeDbId !== roofShapeDbId &&
          propertyData?.project?.sparkelProperties
        ) {
          const otherRefs = propertyData.project.sparkelProperties.filter(
            (prop) =>
              prop.propertySet === 'RoofShapeDbId' &&
              prop.modelUrn === sheetShape.urn &&
              prop.propertyValue === previousRoofShapeDbId.toString() &&
              prop.dbId !== sheetShape.dbId
          );

          if (otherRefs.length === 0) {
            await bulkDeleteSparkelProps({
              input: {
                projectId,
                dbIds: [
                  {
                    modelUrn: sheetShape.urn,
                    dbIds: [previousRoofShapeDbId],
                  },
                ],
                thePropertySet: 'Is Tapered Insulation',
              },
            });
          }
        }

        // Update product areas if provided
        if (productAreas && Object.keys(productAreas).length > 0) {
          const promises = Object.entries(productAreas).map(([sku, area]) =>
            bulkUpsertSparkelProps({
              input: {
                projectId,
                dbIds: [
                  {
                    modelUrn: sheetShape.urn,
                    dbIds: [sheetShape.dbId],
                  },
                ],
                thePropertySet: sku,
                thePropertyValue: area.toString(),
              },
            })
          );
          await Promise.all(promises);
        }

        // Update roof u-value property
        if (roofUValue) {
          await bulkUpsertSparkelProps({
            input: {
              projectId,
              dbIds: [
                {
                  modelUrn: sheetShape.urn,
                  dbIds: [sheetShape.dbId],
                },
              ],
              thePropertySet: 'RoofUValue',
              thePropertyValue: roofUValue.toString(),
            },
          });
        }

        // Update total volume property
        if (totalVolume) {
          await bulkUpsertSparkelProps({
            input: {
              projectId,
              dbIds: [
                {
                  modelUrn: sheetShape.urn,
                  dbIds: [sheetShape.dbId],
                },
              ],
              thePropertySet: 'TotalInsulationVolume',
              thePropertyValue: totalVolume.toString(),
            },
          });
        }
      } catch (error) {
        console.error('Error updating drain line properties:', error);
        toast({
          status: 'error',
          title: 'Error',
          description:
            'Could not update drain line properties. Please try again',
        });
        // Don't re-throw the error to prevent app freezing
      }
    },
    [
      bulkUpsertSparkelProps,
      bulkDeleteSparkelProps,
      propertyData,
      projectId,
      toast,
    ]
  );

  const updateWedgeProperties = useCallback(
    async (
      sheetShape: SheetShapeDeepFragment,
      diamonds: Diamond[],
      kileTypes: KileTypeSKU[],
      totalWedgeProductLength: number
    ) => {
      await updateWedgePropsV2(
        sheetShape,
        diamonds,
        kileTypes,
        totalWedgeProductLength,
        bulkUpsertSparkelProps,
        projectId
      );
    },
    [bulkUpsertSparkelProps, projectId]
  );

  const handleWedgePropertyUpdate = useCallback(
    async (
      sheetShape: SheetShapeDeepFragment,
      wedgeData: {
        diamonds: Diamond[];
        kileTypes: KileTypeSKU[];
        totalWedgeProductLength: number;
      }
    ) => {
      if (!isUpdatingProperties) {
        setIsUpdatingProperties(true);
        try {
          await updateWedgeProperties(
            sheetShape,
            wedgeData.diamonds,
            wedgeData.kileTypes,
            wedgeData.totalWedgeProductLength
          );
        } catch (error) {
          console.error('Error updating wedge properties:', error);
        } finally {
          setIsUpdatingProperties(false);
        }
      }
    },
    [isUpdatingProperties, updateWedgeProperties]
  );

  const handleDrainLinePropertyUpdate = useCallback(
    async (
      sheetShape: SheetShapeDeepFragment,
      drainLineData: {
        crossSection: SlopedInsulationCrossSection;
        roofShapeDbId: number;
        productAreas: Record<string, number>;
        roofUValue: number | undefined;
        totalVolume: number | undefined;
      }
    ) => {
      if (!isUpdatingProperties) {
        setIsUpdatingProperties(true);
        try {
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(
              () => reject(new Error('Drain line properties update timed out')),
              10000
            );
          });

          await Promise.race([
            updateDrainLineProperties(
              sheetShape,
              drainLineData.crossSection,
              drainLineData.roofShapeDbId,
              drainLineData.productAreas,
              drainLineData.roofUValue,
              drainLineData.totalVolume
            ),
            timeoutPromise,
          ]);
        } catch (error) {
          console.error('Error updating drain line properties:', error);
        } finally {
          setIsUpdatingProperties(false);
        }
      }
    },
    [isUpdatingProperties, updateDrainLineProperties]
  );

  // Function to update roof outline properties dynamically
  const updateRoofOutlineProperties = useCallback(
    // eslint-disable-next-line complexity
    async (
      sheetShape: SheetShapeDeepFragment,
      insulationAndFasteners: Product[],
      roofData?: {
        energy: string;
        fireRequirement: string;
        location: string;
        substrate: string;
        roofHeight: number;
        windLoad: number;
      }
    ) => {
      const promises: Promise<void>[] = [];

      // Filter products by type
      const insulationProducts = insulationAndFasteners.filter(
        (product) => product.productType === 'insulation'
      );
      const fastenerProducts = insulationAndFasteners.filter(
        (product) => product.productType === 'fastener'
      );
      const washerProducts = insulationAndFasteners.filter(
        (product) => product.productType === 'washer'
      );

      // Handle insulation products with dynamic layer counting
      let regularInsulationCounter = 1;
      let combustibleInsulationCounter = 1;

      let averageNonCombustibleInsulationHeight = 0;
      let averageCombustibleInsulationHeight = 0;
      let averageNonCombustibleInsulationInFireSectioning = 0;

      for (const insulationProduct of insulationProducts) {
        let propertyName: string;
        let height = insulationProduct.height || 0;

        const isCombustible = insulationProduct.sku.startsWith('SL');

        // Determine property name based on partOfCombustible
        if (
          !insulationProduct.partOfCombustible ||
          insulationProduct.partOfCombustible === InnerOuter.Inner
        ) {
          propertyName = `Insulation Layer ${regularInsulationCounter}`;
          regularInsulationCounter++;
          if (isCombustible) {
            averageCombustibleInsulationHeight += height;
          } else {
            averageNonCombustibleInsulationHeight += height;
          }
        } else if (insulationProduct.partOfCombustible === InnerOuter.Outer) {
          propertyName = `Combustible Insulation Layer ${combustibleInsulationCounter}`;
          combustibleInsulationCounter++;
          averageNonCombustibleInsulationInFireSectioning += height;
        } else {
          // Fallback for any unexpected values
          propertyName = `Insulation Layer ${regularInsulationCounter}`;
          regularInsulationCounter++;
          averageNonCombustibleInsulationHeight += height;
        }

        const promise = updateSparkelProperties(
          sheetShape.dbId,
          propertyName,
          insulationProduct.sku,
          projectId,
          bulkUpsertSparkelProps
        );
        promises.push(promise);
      }

      if (averageNonCombustibleInsulationHeight > 0) {
        const promise = updateSparkelProperties(
          sheetShape.dbId,
          'NonCombustibleInsulationAvgThickness',
          averageNonCombustibleInsulationHeight.toString(),
          projectId,
          bulkUpsertSparkelProps
        );

        promises.push(promise);
      }

      if (averageCombustibleInsulationHeight > 0) {
        const promise = updateSparkelProperties(
          sheetShape.dbId,
          'CombustibleInsulationAvgThickness',
          averageCombustibleInsulationHeight.toString(),
          projectId,
          bulkUpsertSparkelProps
        );
        promises.push(promise);
      }

      if (averageNonCombustibleInsulationInFireSectioning > 0) {
        const promise = updateSparkelProperties(
          sheetShape.dbId,
          'NonCombustibleInsulationInFireSectioningAvgThickness',
          averageNonCombustibleInsulationInFireSectioning.toString(),
          projectId,
          bulkUpsertSparkelProps
        );
        promises.push(promise);
      }

      // Handle fastener products (first one only)
      if (fastenerProducts.length > 0) {
        const promise = updateSparkelProperties(
          sheetShape.dbId,
          'Fastener',
          fastenerProducts[0].sku,
          projectId,
          bulkUpsertSparkelProps
        );
        promises.push(promise);
      }

      // Handle washer products (first one only)
      if (washerProducts.length > 0) {
        const promise = updateSparkelProperties(
          sheetShape.dbId,
          'Washer',
          washerProducts[0].sku,
          projectId,
          bulkUpsertSparkelProps
        );
        promises.push(promise);
      }

      // Update roof data properties
      if (roofData) {
        // Update wind load property
        if (roofData.windLoad) {
          const promise = updateSparkelProperties(
            sheetShape.dbId,
            'QKast',
            roofData.windLoad.toString(),
            projectId,
            bulkUpsertSparkelProps
          );
          promises.push(promise);
        }

        // Update roof height property
        if (roofData.roofHeight) {
          const promise = updateSparkelProperties(
            sheetShape.dbId,
            'Roof height',
            roofData.roofHeight.toString(),
            projectId,
            bulkUpsertSparkelProps
          );
          promises.push(promise);
        }

        if (roofData.energy) {
          const promise = updateSparkelProperties(
            sheetShape.dbId,
            'Energy',
            roofData.energy,
            projectId,
            bulkUpsertSparkelProps
          );

          promises.push(promise);
        }

        if (
          roofData.fireRequirement &&
          roofData.fireRequirement === 'Combustible' &&
          roofData.energy
        ) {
          const promise = updateSparkelProperties(
            sheetShape.dbId,
            'Fire sectioning',
            getSectioning(roofData.energy),
            projectId,
            bulkUpsertSparkelProps
          );

          promises.push(promise);
        } else if (
          roofData.fireRequirement &&
          roofData.fireRequirement === 'Non-Combustible'
        ) {
          const promise = updateSparkelProperties(
            sheetShape.dbId,
            'Fire sectioning',
            '',
            projectId,
            bulkUpsertSparkelProps
          );

          promises.push(promise);
        }

        if (roofData.location) {
          const promise = updateSparkelProperties(
            sheetShape.dbId,
            'Location',
            roofData.location,
            projectId,
            bulkUpsertSparkelProps
          );

          promises.push(promise);
        }

        if (roofData.substrate) {
          const promise = updateSparkelProperties(
            sheetShape.dbId,
            'Substrate',
            roofData.substrate,
            projectId,
            bulkUpsertSparkelProps
          );

          promises.push(promise);
        }
      }

      await Promise.all(promises);
    },
    [bulkUpsertSparkelProps, projectId]
  );

  const handleRoofOutlinePropertyUpdate = useCallback(
    async (
      sheetShape: SheetShapeDeepFragment,
      insulationAndFasteners: Product[] | string,
      roofData?: {
        energy: string;
        fireRequirement: string;
        location: string;
        substrate: string;
        roofHeight: number;
        windLoad: number;
      }
    ) => {
      if (!isUpdatingProperties) {
        setIsUpdatingProperties(true);
        try {
          // Check if we got valid products
          if (typeof insulationAndFasteners === 'string') {
            console.warn(
              'No insulation and fastener products found:',
              insulationAndFasteners
            );
            return;
          }

          if (
            !Array.isArray(insulationAndFasteners) ||
            insulationAndFasteners.length === 0
          ) {
            console.warn('No insulation and fastener products found');
            return;
          }

          await updateRoofOutlineProperties(
            sheetShape,
            insulationAndFasteners,
            roofData
          );
        } catch (error) {
          console.error('Error updating roof outline properties:', error);
        } finally {
          setIsUpdatingProperties(false);
        }
      }
    },
    [isUpdatingProperties, updateRoofOutlineProperties]
  );

  const createShape = useCallback(
    async (
      shapeInput: CreateSheetShapeInput,
      protanData?: {
        drainLineData?: {
          crossSection: SlopedInsulationCrossSection;
          roofShapeDbId: number;
          productAreas: Record<string, number>;
          roofUValue: number | null;
          totalVolume: number | undefined;
        };
        wedgeData?: {
          diamonds: Diamond[];
          kileTypes: KileTypeSKU[];
          totalWedgeProductLength: number;
        };
        roofData?: {
          energy: string;
          fireRequirement: string;
          location: string;
          substrate: string;
          roofHeight: number;
          windLoad: number;
        };
      }
    ) => {
      const result = await createShapeMutation({ input: shapeInput });

      if (result.error) {
        toast({
          status: 'error',
          title: 'Error',
          description: 'Could not create shape. Please try again',
        });
        throw result.error;
      }

      if (!toast.isActive(`shape ${shapeInput.sheetShape.name} created`)) {
        toast({
          id: `shape ${shapeInput.sheetShape.name} created`,
          status: 'success',
          duration: 3000,
          title: `Created shape ${shapeInput.sheetShape.name}`,
        });
      }

      if (!result.data?.createSheetShape?.sheetShape) {
        return result;
      }

      logEvent({
        eventName: 'Shape Created',
        eventType: EventType.Created,
        resourceName: shapeInput.sheetShape.name,
        resourceType: EventResource.SheetShape,
        metadata: {
          affectedResources: [result.data?.createSheetShape?.sheetShape?.id],
          details: `Created ${getShapeType(
            result.data?.createSheetShape?.sheetShape
          )} shape ${shapeInput.sheetShape.name}`,
        },
      });

      const sheetShape = result.data?.createSheetShape?.sheetShape;
      if (!sheetShape) {
        return result;
      }

      // Handle roof data and log insulation and fastener products
      if (protanData?.roofData) {
        const insulationAndFasteners = getInsulationAndFasteners(
          protanData.roofData.energy,
          protanData.roofData.fireRequirement,
          protanData.roofData.location,
          protanData.roofData.substrate
        );

        // Update roof outline properties
        await handleRoofOutlinePropertyUpdate(
          sheetShape,
          insulationAndFasteners,
          protanData.roofData
        );
      }

      // Update properties if needed
      if (protanData?.wedgeData) {
        await handleWedgePropertyUpdate(sheetShape, protanData.wedgeData);
      }

      if (protanData?.drainLineData) {
        await handleDrainLinePropertyUpdate(sheetShape, {
          ...protanData.drainLineData,
          roofUValue: protanData.drainLineData.roofUValue || undefined,
          totalVolume: protanData.drainLineData.totalVolume || undefined,
        });
      }

      return result;
    },
    [
      createShapeMutation,
      logEvent,
      toast,
      handleWedgePropertyUpdate,
      handleDrainLinePropertyUpdate,
      handleRoofOutlinePropertyUpdate,
    ]
  );

  const editShape = useCallback(
    (shape: SheetShapeDeepFragment) => {
      if (!viewer || !activeSheetData) return;

      const converter = new ApiCoordinatesConverter(
        viewer,
        activeSheetData.viewport
      );

      if (shape.sheetShapePolygon) {
        // Handle multipolygons with multiple polygons
        const allPolygons: THREE.Vector3[][] = [];

        shape.sheetShapePolygon.multipolygon.polygons.forEach((polygon) => {
          // Process exterior ring
          const exterior = polygon.exterior.points.map((p) =>
            converter.convertToAutodeskPoint({ x: p.x, y: p.y })
          );
          const closedExterior = [...exterior];
          if (
            closedExterior.length &&
            !closedExterior[0].equals(closedExterior[closedExterior.length - 1])
          ) {
            closedExterior.push(closedExterior[0]);
          }
          allPolygons.push(closedExterior);

          // Process interior rings (holes)
          polygon.interiors.forEach((ring) => {
            const interior = ring.points.map((p) =>
              converter.convertToAutodeskPoint({ x: p.x, y: p.y })
            );
            const closedInterior = [...interior];
            if (
              closedInterior.length &&
              !closedInterior[0].equals(
                closedInterior[closedInterior.length - 1]
              )
            ) {
              closedInterior.push(closedInterior[0]);
            }
            allPolygons.push(closedInterior);
          });
        });

        setInitialPoints(allPolygons);
        setDrawingMode(DrawingMode.Polygon);
        setShapeToEdit(shape);
      } else if (shape.sheetShapeLine) {
        // Check if this is a wedge shape by examining its name
        const isWedgeShape =
          shape.name === 'Wedge' || shape.name.toLowerCase().includes('wedge');

        if (isWedgeShape) {
          // For wedge shapes, just set the points and let the tool handle the rest
          setInitialPoints([
            shape.sheetShapeLine.points.map((p) =>
              converter.convertToAutodeskPoint({ x: p.x, y: p.y })
            ),
          ]);
          setDrawingMode(DrawingMode.Wedges);
          setShapeToEdit(shape);
        } else {
          setInitialPoints([
            shape.sheetShapeLine.points.map((p) =>
              converter.convertToAutodeskPoint({ x: p.x, y: p.y })
            ),
          ]);
          setDrawingMode(DrawingMode.Polyline);
          setShapeToEdit(shape);
        }
      } else if (shape.sheetShapePoint) {
        setInitialPoints([
          [
            converter.convertToAutodeskPoint({
              x: shape.sheetShapePoint.point.x,
              y: shape.sheetShapePoint.point.y,
            }),
          ],
        ]);
        setDrawingMode(DrawingMode.Point);
        setShapeToEdit(shape);
      } else if (shape.sheetShapeSplitLine) {
        setInitialPoints([
          shape.sheetShapeSplitLine.points.map((p) =>
            converter.convertToAutodeskPoint({ x: p.x, y: p.y })
          ),
        ]);
        setDrawingMode(DrawingMode.DrainLine);
        setShapeToEdit(shape);
      }
    },
    [viewer, activeSheetData]
  );

  const createPolygonUpdateInput = useCallback(
    (
      shapeToEdit: SheetShapeDeepFragment,
      results: any
    ): UpdateSheetShapeInput => {
      return {
        id: shapeToEdit.id,
        patch: {
          sheetShapePolygon: {
            updateById: {
              id: shapeToEdit.sheetShapePolygon!.id,
              patch: {
                multipolygon: {
                  polygons: results.multipolygon.polygons.map((poly: any) => ({
                    exterior: {
                      points: poly.exterior.map((pt: any) => ({
                        x: pt[0],
                        y: pt[1],
                      })),
                    },
                    interiors: poly.interiors.map((ring: any) => ({
                      points: ring.map((pt: any) => ({ x: pt[0], y: pt[1] })),
                    })),
                  })),
                },
              },
            },
          },
        },
      };
    },
    []
  );

  const createLineUpdateInput = useCallback(
    (
      shapeToEdit: SheetShapeDeepFragment,
      firstResult: any
    ): UpdateSheetShapeInput => {
      return {
        id: shapeToEdit.id,
        patch: {
          sheetShapeLine: {
            updateById: {
              id: shapeToEdit.sheetShapeLine!.id,
              patch: {
                points: firstResult.points.map((point: any) => ({
                  x: point[0],
                  y: point[1],
                })),
              },
            },
          },
        },
      };
    },
    []
  );

  const createPointUpdateInput = useCallback(
    (
      shapeToEdit: SheetShapeDeepFragment,
      firstResult: any
    ): UpdateSheetShapeInput => {
      return {
        id: shapeToEdit.id,
        patch: {
          sheetShapePoint: {
            updateById: {
              id: shapeToEdit.sheetShapePoint!.id,
              patch: {
                point: {
                  x: firstResult.points[0][0],
                  y: firstResult.points[0][1],
                },
              },
            },
          },
        },
      };
    },
    []
  );

  const createSplitLineUpdateInput = useCallback(
    (
      shapeToEdit: SheetShapeDeepFragment,
      firstResult: any
    ): UpdateSheetShapeInput => {
      // Handle drain line results which have drainLines property
      if (firstResult.drainLines && Array.isArray(firstResult.drainLines)) {
        // Convert drain lines to points array
        const points = firstResult.drainLines.flatMap(
          (segment: [Point2, Point2]) => [
            { x: segment[0][0], y: segment[0][1] },
            { x: segment[1][0], y: segment[1][1] },
          ]
        );

        return {
          id: shapeToEdit.id,
          patch: {
            sheetShapeSplitLine: {
              updateById: {
                id: shapeToEdit.sheetShapeSplitLine!.id,
                patch: {
                  points: points,
                },
              },
            },
          },
        };
      }

      // Handle regular line results which have points property
      if (firstResult.points && Array.isArray(firstResult.points)) {
        return {
          id: shapeToEdit.id,
          patch: {
            sheetShapeSplitLine: {
              updateById: {
                id: shapeToEdit.sheetShapeSplitLine!.id,
                patch: {
                  points: firstResult.points.map((point: any) => ({
                    x: point.x,
                    y: point.y,
                  })),
                },
              },
            },
          },
        };
      }

      throw new Error('Invalid result format for split line update');
    },
    []
  );

  // eslint-disable-next-line complexity
  const updateShape = useCallback(
    // eslint-disable-next-line complexity
    async (protanData?: {
      drainLineData?: {
        crossSection: SlopedInsulationCrossSection;
        roofShapeDbId: number;
        productAreas: Record<string, number>;
        roofUValue: number | null;
      };
      wedgeData?: {
        diamonds: Diamond[];
        kileTypes: KileTypeSKU[];
        totalWedgeProductLength: number;
      };
      roofData?: {
        energy: string;
        fireRequirement: string;
        location: string;
        substrate: string;
        roofHeight: number;
        windLoad: number;
      };
    }) => {
      if (!shapeToEdit || !results) return;

      // Check if results are valid
      const isValid = Array.isArray(results)
        ? results.length > 0 && results.every((r) => r.valid)
        : results.valid;

      if (!isValid) return;

      let input: UpdateSheetShapeInput;

      // Handle polygon shapes
      if ('multipolygon' in results) {
        input = createPolygonUpdateInput(shapeToEdit, results);
      }
      // Handle line and point shapes
      else if (Array.isArray(results) && results.length > 0) {
        const firstResult = results[0];

        if (
          drawingMode === DrawingMode.Polyline &&
          shapeToEdit.sheetShapeLine
        ) {
          input = createLineUpdateInput(shapeToEdit, firstResult);
        } else if (
          drawingMode === DrawingMode.Wedges &&
          shapeToEdit.sheetShapeLine
        ) {
          input = createLineUpdateInput(shapeToEdit, firstResult);
        } else if (
          drawingMode === DrawingMode.Point &&
          shapeToEdit.sheetShapePoint
        ) {
          input = createPointUpdateInput(shapeToEdit, firstResult);
        } else if (
          drawingMode === DrawingMode.DrainLine &&
          shapeToEdit.sheetShapeSplitLine
        ) {
          // For drain lines, use the tool's results directly since they contain the drainLines property
          if (toolRef.current && 'getResults' in toolRef.current) {
            const drainLineResults = (
              toolRef.current as DrainLineDrawingTool
            ).getResults();

            if (drainLineResults.valid) {
              // Create a proper DrainLineDrawingResult for the update
              const drainLineDrawingResult = {
                type: ShapeDrawingResultType.DrainLine,
                drainLines: drainLineResults.drainLines,
                valid: drainLineResults.valid,
                crossSection: drainLineResults.crossSection,
                roofShapeDbId: drainLineResults.roofShapeDbId,
                productAreas: drainLineResults.productAreas,
                totalVolume: drainLineResults.totalVolume,
              };
              input = createSplitLineUpdateInput(
                shapeToEdit,
                drainLineDrawingResult
              );
            } else {
              console.error('Drain line results are not valid');
              return;
            }
          } else {
            console.error('Drain line tool not available for update');
            return;
          }
        } else {
          console.error('Unsupported shape type for update');
          return;
        }
      } else {
        console.error('Invalid results format for update');
        return;
      }

      const result = await updateShapeMutation({ input });

      if (!result.error) {
        // For roof outline polygon/rectangle/magic polygon shapes, also update the properties if provided
        if (protanData?.roofData && result.data?.updateSheetShape?.sheetShape) {
          try {
            const insulationAndFasteners = getInsulationAndFasteners(
              protanData.roofData.energy,
              protanData.roofData.fireRequirement,
              protanData.roofData.location,
              protanData.roofData.substrate
            );
            await handleRoofOutlinePropertyUpdate(
              result.data.updateSheetShape.sheetShape,
              insulationAndFasteners,
              protanData.roofData
            );
          } catch (error) {
            console.error(
              'Error updating roof outline properties during shape update:',
              error
            );
            // Don't fail the entire update if property update fails
          }
        }

        // For drain line shapes, also update the properties
        if (drawingMode === DrawingMode.DrainLine && drainLineData) {
          try {
            await updateDrainLineProperties(
              shapeToEdit,
              drainLineData.crossSection,
              drainLineData.roofShapeDbId,
              drainLineData.productAreas,
              drainLineData.roofUValue || undefined,
              drainLineData.totalVolume
            );
          } catch (error) {
            console.error(
              'Error updating drain line properties during shape update:',
              error
            );
            // Don't fail the entire update if property update fails
          }
        }

        // For wedge shapes, also update the properties
        if (
          drawingMode === DrawingMode.Wedges &&
          results &&
          Array.isArray(results) &&
          results.length > 0
        ) {
          try {
            const firstResult = results[0] as any;
            if (
              firstResult.wedgeData &&
              result.data?.updateSheetShape?.sheetShape
            ) {
              await updateWedgeProperties(
                result.data.updateSheetShape.sheetShape,
                firstResult.wedgeData.diamonds,
                firstResult.wedgeData.kileTypes,
                firstResult.wedgeData.totalWedgeProductLength
              );
            }
          } catch (error) {
            console.error(
              'Error updating wedge properties during shape update:',
              error
            );
            // Don't fail the entire update if property update fails
          }
        }

        // Clean up state after successful update
        setShapeToEdit(null);
        setInitialPoints(null);
        setResults(undefined);
        setDrainLineData(null);

        logEvent({
          eventName: 'Shape Updated',
          eventType: EventType.Modified,
          resourceName: shapeToEdit.name,
          resourceType: EventResource.SheetShape,
          metadata: {
            affectedResources: [shapeToEdit.id],
          },
        });
      }

      return result;
    },
    [
      shapeToEdit,
      results,
      updateShapeMutation,
      createPolygonUpdateInput,
      drawingMode,
      createLineUpdateInput,
      createPointUpdateInput,
      logEvent,
      createSplitLineUpdateInput,
      drainLineData,
      updateDrainLineProperties,
      updateWedgeProperties,
      handleRoofOutlinePropertyUpdate,
    ]
  );

  // The return value of this hook is passed into a context provider.
  // It is memoized to prevent consumers of the context from re-rendering
  // unnecessarily, which was causing an infinite render loop.
  return useMemo(
    () => ({
      isDrawing,
      drawingMode,
      setDrawingMode,
      results,
      hasResults,
      createShape,
      createShapeResult,
      editShape,
      updateShape,
      updateShapeResult,
      shapeToEdit,
      isMagicPolygonLoading,
      drainLineData,
      setDrainLineData,
      minHeight,
      setMinHeight,
    }),
    [
      isDrawing,
      drawingMode,
      setDrawingMode,
      results,
      hasResults,
      createShape,
      createShapeResult,
      editShape,
      updateShape,
      updateShapeResult,
      shapeToEdit,
      isMagicPolygonLoading,
      drainLineData,
      setDrainLineData,
      minHeight,
      setMinHeight,
    ]
  );
}
